import type { ItemSchedule } from "~/domain/item-schedule";
import { useOutletStore } from "~/stores/outlet";
import { isUnderMinimumLeadTime } from "~/utils/scheduling";

/**
 * Composable for handling minimum lead time calculations for menu items.
 * Determines which items should be disabled based on their minimum lead time requirements
 * and the scheduled order time.
 *
 * @returns Object containing utility functions for minimum lead time operations
 */
export const useMinimumLeadTime = () => {
  /**
   * Calculates minimum lead time conflicts from a pre-fetched list of item schedules.
   * This is useful when you've already loaded all schedules and want to calculate conflicts
   * without making additional API calls.
   *
   * @param itemSchedules - Pre-fetched array of item schedules
   * @param scheduledTime - Optional scheduled order time with day and time.
   *                        When undefined, the current time (now) is used.
   * @param plus - Optional array of PLUs to filter. If provided, only these items are checked.
   * @returns Record mapping PLU to minimum lead time in minutes for items that should be disabled.
   *
   * @example
   * ```ts
   * const { calculateConflictsFromSchedules } = useMinimumLeadTime();
   * const conflicts = calculateConflictsFromSchedules(
   *   schedules,
   *   { day: '2026-01-25', time: '18:00' },
   *   ['ITEM001', 'ITEM002']
   * );
   * ```
   */
  const calculateConflictsFromSchedules = (
    itemSchedules: ItemSchedule[],
    scheduledTime?: { day: string; time: string },
    plus?: string[],
  ): Record<string, number> => {
    const schedulesToCheck = plus
      ? itemSchedules.filter((schedule) => plus.includes(schedule.plu))
      : itemSchedules;

    const outletStore = useOutletStore();

    return schedulesToCheck.reduce(
      (acc, itemSchedule) => {
        const { minimumLeadTimeInMinutes } = itemSchedule.settings;

        const shouldDisable = isUnderMinimumLeadTime(
          minimumLeadTimeInMinutes,
          scheduledTime,
          outletStore.timezone,
        );

        if (!shouldDisable) return acc;

        return {
          ...acc,
          [itemSchedule.plu]: minimumLeadTimeInMinutes,
        };
      },
      {} as Record<string, number>,
    );
  };

  /**
   * Validates if items in a basket can be scheduled for a given time based on minimum lead time.
   *
   * @param items - Array of items with plu and name properties
   * @param scheduledTime - Optional scheduled order time with day and time
   * @param formatError - Function to format error message for a given item name
   * @param itemSchedules - Optional pre-fetched item schedules. Required for validation.
   * @returns Object with isValid boolean and errors string (null if valid)
   *
   * @example
   * ```ts
   * const { validateScheduleTime } = useMinimumLeadTime();
   * const result = validateScheduleTime(
   *   basket.items,
   *   { day: '2026-01-25', time: '18:00' },
   *   (itemName) => t('error.item_conflict', { itemName }),
   *   cachedSchedules
   * );
   * if (!result.isValid) {
   *   console.error(result.errors);
   * }
   * ```
   */
  const validateScheduleTime = (
    items: Array<{ plu: string; name: string }>,
    scheduledTime: { day: string; time: string } | undefined,
    formatError: (itemName: string) => string,
    itemSchedules?: ItemSchedule[],
  ): { isValid: false; errors: string } | { isValid: true; errors: null } => {
    const itemsPlus = items.map((item) => item.plu);

    if (!itemSchedules || itemSchedules.length === 0)
      return { isValid: true, errors: null };

    const itemsMinimumLeadTime = calculateConflictsFromSchedules(
      itemSchedules,
      scheduledTime,
      itemsPlus,
    );

    const hasMinimumLeadTimeIssues =
      Object.keys(itemsMinimumLeadTime).length > 0;

    if (hasMinimumLeadTimeIssues) {
      const errors = Object.entries(itemsMinimumLeadTime)
        .map(([plu]) => {
          const itemName = items.find((item) => item.plu === plu)?.name || "";
          return formatError(itemName);
        })
        .join("\n");

      return { isValid: false, errors };
    }

    return { isValid: true, errors: null };
  };

  return {
    calculateConflictsFromSchedules,
    validateScheduleTime,
  };
};
